import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

export const DEFAULT_AUTH_GATE_MESSAGE =
  "Create a free account or sign in to continue.";

const getReturnState = (location) => ({
  from: {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  },
});

const AuthActionGateContext = createContext(null);

export const SignInRequiredCard = ({
  title = "Sign in to continue",
  message = DEFAULT_AUTH_GATE_MESSAGE,
}) => {
  const location = useLocation();
  const returnState = getReturnState(location);

  return (
    <main className="page-shell page-shell-narrow">
      <section className="card blocked-account-card" aria-live="polite">
        <p className="eyebrow">Member area</p>
        <h2>{title}</h2>
        <p>{message}</p>
        <div className="button-group">
          <Link className="btn-primary" to="/register" state={returnState}>
            Create Free Account
          </Link>
          <Link className="btn-soft" to="/login" state={returnState}>
            Sign In
          </Link>
          <Link className="btn-soft" to="/home">
            Cancel
          </Link>
        </div>
      </section>
    </main>
  );
};

export const AuthActionGate = ({
  open,
  onClose,
  title = "Sign in to continue",
  message = DEFAULT_AUTH_GATE_MESSAGE,
}) => {
  const context = useContext(AuthActionGateContext);

  if (context) return null;

  return (
    <AuthActionGateModal
      open={open}
      onClose={onClose}
      title={title}
      message={message}
    />
  );
};

const AuthActionGateModal = ({
  open,
  onClose,
  title = "Sign in to continue",
  message = DEFAULT_AUTH_GATE_MESSAGE,
}) => {
  const location = useLocation();
  const returnState = getReturnState(location);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="modal auth-action-gate"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="product-detail-modal auth-action-gate-card"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="eyebrow">Account required</p>
        <h2>{title}</h2>
        <p>{message}</p>
        <div className="button-group auth-action-gate-actions">
          <Link className="btn-primary" to="/register" state={returnState}>
            Create Free Account
          </Link>
          <Link className="btn-soft" to="/login" state={returnState}>
            Sign In
          </Link>
          <button type="button" className="btn-soft" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const useLocalAuthActionGate = (currentUser) => {
  const [gateMessage, setGateMessage] = useState("");
  const closeGate = useCallback(() => setGateMessage(""), []);

  const requireSignIn = useCallback((message = DEFAULT_AUTH_GATE_MESSAGE) => {
    if (currentUser?.uid) return true;
    setGateMessage(message);
    return false;
  }, [currentUser?.uid]);

  return {
    requireSignIn,
    gateProps: {
      open: Boolean(gateMessage),
      message: gateMessage || DEFAULT_AUTH_GATE_MESSAGE,
      onClose: closeGate,
    },
  };
};

export const AuthActionGateProvider = ({ currentUser, children }) => {
  const [gateMessage, setGateMessage] = useState("");
  const closeGate = useCallback(() => setGateMessage(""), []);
  const requireSignIn = useCallback((message = DEFAULT_AUTH_GATE_MESSAGE) => {
    if (currentUser?.uid) return true;
    setGateMessage(message);
    return false;
  }, [currentUser?.uid]);

  useEffect(() => {
    if (currentUser?.uid) closeGate();
  }, [closeGate, currentUser?.uid]);

  const gateProps = {
    open: Boolean(gateMessage),
    message: gateMessage || DEFAULT_AUTH_GATE_MESSAGE,
    onClose: closeGate,
  };

  return (
    <AuthActionGateContext.Provider value={{ requireSignIn, gateProps }}>
      {children}
      <AuthActionGateModal {...gateProps} />
    </AuthActionGateContext.Provider>
  );
};

export const useAuthActionGate = (currentUser) => {
  const context = useContext(AuthActionGateContext);
  const localGate = useLocalAuthActionGate(currentUser);
  return context || localGate;
};
